import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import Icon from '../components/Icon';
import FunnelWaitVideo from '../components/FunnelWaitVideo';
import FunnelWelcomePage from './FunnelWelcomePage';
import { isExistingAccount } from './FunnelAuthPage';
import { useFunnelAnalytics } from '../hooks/useFunnelAnalytics';
import { EVENTS } from '../constants/events';

/**
 * Authentication step for the SECOND RevenueCat Web Funnel — the cold-traffic
 * one, aimed at parents who have never heard of Pixley.
 *
 * It runs the same flow as /funnel-auth (FunnelAuthPage) — the shared welcome
 * screen, then name → screen-time struggle → build a character → sign in — with
 * one extra screen wedged in up front: a "how Pixley works" explainer between
 * the welcome and the first question. That screen is the whole difference. This
 * traffic arrives with no idea what Pixley is, so it gets told before it's
 * asked for anything.
 *
 * The steps themselves are copies of the /funnel-auth ones, deliberately: the
 * two funnels are an experiment on the explainer, and /funnel-auth is the
 * control, so nothing here reaches into it beyond the two pieces it exports
 * (isExistingAccount, FunnelWelcomePage).
 *
 * Everything either side of those screens is the same RevenueCat contract the
 * other funnel implements: arrive with `redirect_uri` + `state`, sign in, hand
 * back the `state` unchanged plus our `app_user_id`.
 * https://www.revenuecat.com/docs/tools/funnels/creating-funnels#authentication-steps
 */

// sessionStorage. Namespaced away from the /funnel-auth keys (`rc_funnel_*`):
// a visitor can meet both funnels in one tab, and neither a step name nor a
// half-finished answer from one flow means anything to the other.
const STEP_KEY = 'rc_funnel2_step';
const STARTED_KEY = 'rc_funnel2_started';
const NAME_KEY = 'rc_funnel2_child_name';
const PREVIEW_KEY = 'rc_funnel2_preview_img';
// Written by AuthContext during sign-in: whether the users row existed before
// that sign-in auto-created it. Shared with the other funnel by design — it
// describes the account, not the flow.
const SIGNIN_USER_EXISTED_KEY = 'pixley_signin_user_existed';

const clearIntroFunnelSession = () =>
  [
    STEP_KEY, STARTED_KEY, NAME_KEY,
    PREVIEW_KEY, SIGNIN_USER_EXISTED_KEY,
  ].forEach((k) => sessionStorage.removeItem(k));

// iOS onboarding theme (NewOnboardingKit.swift), same palette as /funnel-auth
const ACCENT = '#0A6CFF';
const CONFETTI_COLORS = [ACCENT, '#FFC83D', '#7C5CFF', '#34C759', '#FF6FA5'];

// Ready-made friends for visitors with no photo handy. Same set and same
// 768px webp assets as /funnel-auth — `key` matches the backend's
// DEFAULT_AVATAR_KEYS (Pixley-Backend/routers/profiles_router.py), so both
// funnels and the app offer one set of starter characters, not three.
const PRESET_FRIENDS = [
  { key: 'zippy-icon', name: 'Zippy', kind: 'tiger' },
  { key: 'benny', name: 'Benny', kind: 'bunny' },
  { key: 'willow', name: 'Willow', kind: 'deer' },
];

// The three explainer slides lead, because the welcome screen is a full-page
// takeover ahead of the shell (same as /funnel-auth) and so isn't a numbered
// step.
const STEP_ORDER = ['friend', 'episode', 'personal', 'name', 'create', 'meet', 'signin'];
// Incl. the transient views ('welcome', 'generating', 'finishing') that no one
// clicks but everyone passes through — used for the slide direction and for the
// analytics step numbering.
const FLOW_ORDER = [
  'welcome', 'friend', 'episode', 'personal', 'name', 'create',
  'generating', 'meet', 'signin', 'finishing',
];
const BACK_TARGET = {
  episode: 'friend', personal: 'episode', name: 'personal',
  create: 'name', signin: 'meet',
};

// ── the three explainer slides ─────────────────────────────────────────────
// Lifted from the marketing site's three feature sections (LandingPage.js):
// same clips, same headlines, same body copy and same three proof points, so a
// visitor who saw the landing page meets the identical pitch here and the two
// can't drift into promising different things.
//
// `accent` is the word the landing page paints with the blue→cyan gradient.
// Split into three parts rather than parsed out of one string because the
// accent sits mid-sentence on the second slide.
//
// Sizes matter here: this is the top of a funnel on a phone. The clips are
// 0.4MB, 5.3MB and (slide three) 0.4-2.0MB, so only the current slide's video
// is ever mounted and the next one is prefetched a screen ahead — see
// SLIDE_PREFETCH below.
const SLIDES = [
  {
    key: 'friend',
    eyebrow: 'Create a character',
    title: { pre: 'Meet their next ', accent: 'best friend', post: '' },
    cta: 'See how stories work',
    // Framed clip: opaque, so it gets the rounded card treatment the landing
    // page gives it. `rate: 2` matches the landing page's playbackRate.
    video: { src: '/assets/final_create_character_cropped.mp4', rate: 2, tilt: -1.5 },
  },
  {
    key: 'episode',
    eyebrow: 'Create a story',
    title: { pre: 'From idea to ', accent: 'episode', post: ' in minutes' },
    cta: 'One more thing',
    video: { src: '/assets/create_story_video_cropped.mp4', rate: 2, tilt: 1.5 },
  },
  {
    key: 'personal',
    eyebrow: 'Why parents pick Pixley',
    title: { pre: "The world's most ", accent: 'personal', post: ' storytelling app' },
    cta: "Let's begin",
    // No `video`: this slide swaps between two alpha-channel clips in sync with
    // the rotating word, exactly like the landing page section it comes from.
    video: null,
  },
];

const SLIDE_BY_KEY = Object.fromEntries(SLIDES.map((s) => [s.key, s]));

// Slide three's rotating word, and the transparent-background clip paired with
// each — .mov (HEVC alpha) for Safari/iOS, .webm (VP9 alpha) elsewhere. Same
// pairing and same tilts as the landing page.
const STORYTELLER_MEDIA = [
  { word: 'storybooks', mov: '/assets/books-nobg.mov', webm: '/assets/books-nobg.webm', tilt: -2 },
  { word: 'stories', mov: '/assets/video-nobg.mov', webm: '/assets/video-nobg.webm', tilt: 2 },
];

// Which clip to warm while a given slide is on screen, always one screen ahead
// of where it is needed.
//
// Entries with `mov`/`webm` are the alpha clips, and which one to fetch depends
// on the browser — prefetching both would pull the file the visitor will never
// play. That matters most on iOS, this funnel's main audience: Safari plays the
// .movs, which are 8x the size of the .webms (3.3MB vs 0.4MB), so prefetching
// the webm there would leave the real download to happen on arrival.
//
// Slide three's two clips are split across two steps rather than fetched
// together, so Safari pulls 3.3MB then 4.1MB instead of 7.4MB at once. The
// second is only needed 3s after the slide appears, when the word first flips.
const SLIDE_PREFETCH = {
  friend: [{ src: '/assets/create_story_video_cropped.mp4' }],
  episode: [STORYTELLER_MEDIA[0]],
  personal: [STORYTELLER_MEDIA[1]],
};

// Safari reports "probably"/"maybe" for HEVC; everything else reports "".
// Evaluated once, lazily, since it touches the DOM.
let hevcSupport = null;
const prefersHevcAlpha = () => {
  if (hevcSupport === null) {
    try {
      hevcSupport = !!document.createElement('video').canPlayType('video/mp4; codecs="hvc1"');
    } catch (_) {
      hevcSupport = false;
    }
  }
  return hevcSupport;
};

// Resolve a prefetch entry to the one URL this browser will actually play
const prefetchUrl = (entry) =>
  entry.src || (prefersHevcAlpha() ? entry.mov : entry.webm);

// Rotating status lines while the character generates
const GEN_MESSAGES = [
  'Pixley is sketching the outlines…',
  'Mixing the perfect colors…',
  'Adding a spark of magic…',
  'Putting on the finishing touches…',
];

// ── motion variants (step slide + staggered content reveal) ────────────────
const stepVariants = {
  enter: (dir) => ({ opacity: 0, x: dir >= 0 ? 56 : -56, scale: 0.98 }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir >= 0 ? -56 : 56,
    scale: 0.98,
    transition: { duration: 0.22, ease: 'easeIn' },
  }),
};

const itemVariants = {
  enter: { opacity: 0, y: 18 },
  center: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

// Ambient background — soft drifting color blobs + rising sparkle dots
const AMBIENT_DOTS = [
  { left: '6%', size: 6, delay: 0, dur: 16, color: 'rgba(10,108,255,0.35)' },
  { left: '16%', size: 4, delay: 5, dur: 21, color: 'rgba(255,200,61,0.45)' },
  { left: '28%', size: 5, delay: 9, dur: 18, color: 'rgba(124,92,255,0.35)' },
  { left: '44%', size: 4, delay: 2, dur: 23, color: 'rgba(10,108,255,0.30)' },
  { left: '58%', size: 6, delay: 12, dur: 19, color: 'rgba(255,111,165,0.35)' },
  { left: '70%', size: 4, delay: 6, dur: 17, color: 'rgba(10,108,255,0.40)' },
  { left: '82%', size: 5, delay: 10, dur: 22, color: 'rgba(255,200,61,0.40)' },
  { left: '92%', size: 4, delay: 3, dur: 20, color: 'rgba(124,92,255,0.40)' },
];

const AmbientBackground = () => (
  <div className="fi-ambient" aria-hidden="true">
    <span className="fi-blob fi-blob-1" />
    <span className="fi-blob fi-blob-2" />
    <span className="fi-blob fi-blob-3" />
    {AMBIENT_DOTS.map((d, i) => (
      <span
        key={i}
        className="fi-dot"
        style={{
          left: d.left,
          width: d.size,
          height: d.size,
          background: d.color,
          animationDelay: `${d.delay}s`,
          animationDuration: `${d.dur}s`,
        }}
      />
    ))}
  </div>
);

// ── image helpers ──────────────────────────────────────────────────────────

// Downscale a Blob to a JPEG data URL small enough for sessionStorage
const blobToDataURL = (blob, maxDim = 768) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

const dataURLtoBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/data:(.*?);/)?.[1] || 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

// ── celebration bits (confetti + sparkles, from NOMeetPixleyStep) ──────────

const Confetti = () => {
  const pieces = useRef(
    Array.from({ length: 45 }, (_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      dur: 2.2 + Math.random() * 1.6,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      drift: (Math.random() - 0.5) * 160,
      spin: 360 + Math.random() * 360,
    }))
  ).current;
  return (
    <div className="fi-confetti" aria-hidden="true">
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
            '--drift': `${p.drift}px`,
            '--spin': `${p.spin}deg`,
          }}
        />
      ))}
    </div>
  );
};

const SPARKLES = [
  { top: '-4%', left: '8%', size: 16, color: ACCENT },
  { top: '10%', left: '96%', size: 12, color: '#FFC83D' },
  { top: '46%', left: '-6%', size: 10, color: '#FFC83D' },
  { top: '68%', left: '102%', size: 14, color: ACCENT },
  { top: '96%', left: '14%', size: 11, color: ACCENT },
  { top: '88%', left: '84%', size: 13, color: '#FFC83D' },
];

const Sparkles = () => (
  <>
    {SPARKLES.map((s, i) => (
      <span
        key={i}
        className="fi-sparkle"
        style={{
          top: s.top,
          left: s.left,
          color: s.color,
          animationDelay: `${i * 0.18}s`,
        }}
        aria-hidden="true"
      >
        <Icon name="auto_awesome" size={s.size} fill />
      </span>
    ))}
  </>
);

/* One explainer slide: the clip, the landing page's headline, the button.
   Slide three is the odd one out — its media is two alpha-channel clips that
   cross-fade rather than one looping video, so it takes `clipIdx` from the
   parent, which drives the 3s timer and only while that slide is on screen. */
const SlideScreen = ({ slide, clipIdx, onContinue }) => (
  <>
    <motion.div variants={itemVariants} className="fi-slide-stage">
      {slide.video ? (
        <motion.div
          className="fi-slide-frame"
          initial={{ opacity: 0, y: 26, rotate: 0 }}
          animate={{ opacity: 1, y: 0, rotate: slide.video.tilt }}
          transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            animate={{ y: [0, -7, 0] }}
            transition={{ duration: 5.4, repeat: Infinity, ease: 'easeInOut', delay: 0.7 }}
          >
            <video
              className="fi-slide-video"
              autoPlay
              muted
              loop
              playsInline
              // Matches the landing page, where these clips also run at 2x
              ref={(el) => el && (el.playbackRate = slide.video.rate)}
              src={slide.video.src}
            />
          </motion.div>
        </motion.div>
      ) : (
        <div className="fi-clipstage">
          <span className="fi-clipstage-glow" aria-hidden="true" />
          <span className="fi-clipstage-ground" aria-hidden="true" />
          {STORYTELLER_MEDIA.map((m, i) => {
            const active = clipIdx === i;
            return (
              <motion.div
                key={m.word}
                className="fi-clipstage-clip"
                initial={false}
                animate={{
                  opacity: active ? 1 : 0,
                  scale: active ? 1 : 0.93,
                  rotate: active ? m.tilt : 0,
                  y: active ? 0 : 12,
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                  opacity: { duration: 0.35, delay: active ? 0.12 : 0 },
                }}
                aria-hidden={!active}
                style={{ zIndex: active ? 2 : 1 }}
              >
                {/* .mov (HEVC alpha) for Safari/iOS, .webm (VP9 alpha) elsewhere */}
                <video autoPlay muted loop playsInline>
                  <source src={m.mov} type='video/mp4; codecs="hvc1"' />
                  <source src={m.webm} type="video/webm" />
                </video>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>

    <motion.div variants={itemVariants} className="fi-eyebrow">{slide.eyebrow}</motion.div>
    <motion.h1 variants={itemVariants} className="fi-title fi-slide-title">
      {slide.title.pre}
      <span className="fi-accent">{slide.title.accent}</span>
      {slide.title.post}
    </motion.h1>

    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      <button className="fi-primary-btn" onClick={onContinue}>
        {slide.cta} <span className="fi-chevron">›</span>
      </button>
    </motion.div>
  </>
);

const FunnelIntroAuthPage = () => {
  const [searchParams] = useSearchParams();
  const { signInWithGoogle, isAuthenticated, loading, user } = useAuth();
  const { apiClient } = useAPI();
  const hasCompletedRef = useRef(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const [step, setStepState] = useState(() => {
    const saved = sessionStorage.getItem(STEP_KEY);
    // 'generating' can't be resumed after a reload — restart from create
    if (saved === 'generating') return 'create';
    return saved && FLOW_ORDER.includes(saved) ? saved : 'friend';
  });
  const [childName, setChildName] = useState(() => sessionStorage.getItem(NAME_KEY) || '');
  const [previewImage, setPreviewImage] = useState(() => sessionStorage.getItem(PREVIEW_KEY) || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  // Key of the ready-made friend currently being loaded (null = none)
  const [loadingFriend, setLoadingFriend] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [genMsgIndex, setGenMsgIndex] = useState(0);
  const [heroRevealed, setHeroRevealed] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showExistingAccount, setShowExistingAccount] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem(STEP_KEY));
  // Which of slide three's two clips is showing. Lives here rather than in
  // SlideScreen so the timer below can be scoped to the one step that shows it.
  const [clipIdx, setClipIdx] = useState(0);

  const funnel = useFunnelAnalytics(
    {},
    {
      funnelName: 'web2app_intro',
      viewOrder: FLOW_ORDER,
      pathPrefix: '/funnel-intro',
      startedKey: STARTED_KEY,
    }
  );

  // Slide direction: moving forward slides left, going back slides right
  const prevStepRef = useRef(step);
  const direction = FLOW_ORDER.indexOf(step) >= FLOW_ORDER.indexOf(prevStepRef.current) ? 1 : -1;
  useEffect(() => {
    prevStepRef.current = step;
  }, [step]);

  const setStep = useCallback((next) => {
    sessionStorage.setItem(STEP_KEY, next);
    setStepState(next);
    window.scrollTo({ top: 0 });
  }, []);

  // Existing-account modal lingers a moment before leaving for the main site
  const redirectTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(redirectTimerRef.current), []);

  // Cross-fade slide three's two clips every 3s, same cadence as the landing
  // page, and only while that slide is up — an interval running behind the
  // sign-in step would re-render it for nothing.
  useEffect(() => {
    if (step !== 'personal') return undefined;
    const id = setInterval(() => setClipIdx((p) => (p + 1) % STORYTELLER_MEDIA.length), 3000);
    return () => clearInterval(id);
  }, [step]);

  // Warm the next slide's clip while the current one plays. Only the visible
  // slide's <video> is ever mounted — mounting them all would pull megabytes
  // before the visitor has read a word — so without this each clip would start
  // downloading only once its screen appeared. Detached elements are held in a
  // ref so the buffering isn't garbage collected; the real <video> then plays
  // from the media cache. See SLIDE_PREFETCH for what is fetched when.
  const prefetchedRef = useRef(new Set());
  const prefetchHoldRef = useRef([]);
  useEffect(() => {
    (SLIDE_PREFETCH[step] || []).map(prefetchUrl).forEach((url) => {
      if (!url || prefetchedRef.current.has(url)) return;
      prefetchedRef.current.add(url);
      const video = document.createElement('video');
      video.muted = true;
      video.preload = 'auto';
      video.src = url;
      video.load();
      prefetchHoldRef.current.push(video);
    });
  }, [step]);

  // The name is never sent to analytics — only its length, so a truncated or
  // placeholder entry is still visible in the drop-off data.
  const submitName = (method) => {
    funnel.trackStepCompleted('name', {
      method,
      name_length: childName.trim().length,
    });
    setStep('create');
  };

  // Funnel params. On first arrival RevenueCat passes `redirect_uri`/`state`;
  // after the Google round trip we land back here with the same values under
  // `funnel_redirect_uri`/`funnel_state` (prefixed so they don't collide with
  // the OAuth `state`/`code` Supabase appends).
  const redirectUri =
    searchParams.get('redirect_uri') || searchParams.get('funnel_redirect_uri');
  const funnelState =
    searchParams.get('state') || searchParams.get('funnel_state');

  const invalidLink = !redirectUri || !funnelState;

  // ── analytics: one step_viewed + virtual pageview per view ────────────────
  useEffect(() => {
    if (invalidLink) {
      funnel.trackInvalidLink();
      return;
    }
    funnel.trackView(showWelcome ? 'welcome' : step);
  }, [showWelcome, step, invalidLink, funnel]);

  // Buffer the generating-step panda video while they pick a photo — otherwise
  // it only starts downloading when the wait screen mounts, which on a slow
  // connection means a blank circle at the funnel's most anxious moment. The
  // detached element is held in a ref so buffering isn't GC'd; the wait screen's
  // <video> then plays from the media cache instantly.
  const prefetchVideoRef = useRef(null);
  useEffect(() => {
    if (step !== 'create' || prefetchVideoRef.current) return;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    [
      { src: '/assets/loading-panda.webm', type: 'video/webm' },
      { src: '/assets/loading-panda.mp4', type: 'video/mp4' },
    ].forEach(({ src, type }) => {
      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      video.appendChild(source);
    });
    video.load();
    prefetchVideoRef.current = video;
  }, [step]);

  // Warm the API connection (DNS + TCP + TLS) the moment a photo is chosen —
  // generation is one tap away, and a real request beats a preconnect hint that
  // would have idled out minutes before the user got here.
  const apiWarmedRef = useRef(false);
  useEffect(() => {
    if (!photoFile || apiWarmedRef.current || !apiClient) return;
    apiWarmedRef.current = true;
    apiClient.healthCheck().catch(() => {});
  }, [photoFile, apiClient]);

  // ── completion: save the held character, then bounce back to the funnel ───
  const completeAndRedirect = useCallback(async () => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    const returnToFunnel = async ({ isExisting }) => {
      try {
        const result = await apiClient.completeFunnelAuth(redirectUri, funnelState);
        if (result.status && result.data?.redirect_url) {
          logger.log('✅ Intro funnel auth complete, returning to funnel');
          // Unbatched: the redirect below can outrun PostHog's next flush, and
          // this is the conversion event.
          funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
            outcome: 'funnel_checkout',
            is_existing_account: isExisting,
            has_character: !!sessionStorage.getItem(PREVIEW_KEY),
          });
          clearIntroFunnelSession();
          setShowSuccess(true);
          window.location.replace(result.data.redirect_url);
        } else {
          throw new Error(result.error || 'Failed to complete funnel authentication');
        }
      } catch (error) {
        logger.error('❌ Intro funnel auth completion error:', error);
        funnel.track(EVENTS.FUNNEL_COMPLETION_FAILED, {
          is_existing_account: isExisting,
          error_message: error.message || 'unknown',
          status_code: error.status || null,
        });
        hasCompletedRef.current = false;
        setIsSigningIn(false);
        setStep('signin');
        showToast(error.message || 'Something went wrong. Please try again.', 'error');
      }
    };

    // Same rule as /funnel-auth: the funnel is for new sign-ups, so a returning
    // account only skips it when RevenueCat says they're actually subscribed.
    // Everyone else continues to checkout.
    if (isExistingAccount(user)) {
      let isSubscribed = false;
      try {
        const sub = await apiClient.getSubscriptionStatus(user.id, { force: true });
        const tier = sub?.data?.subscription;
        isSubscribed = Boolean(sub?.data?.premium) || (!!tier && tier !== 'NONE');
      } catch (error) {
        // Don't shortcut someone home on an unverified subscription — on an
        // RC/backend hiccup, let them continue through checkout.
        logger.warn('Intro funnel: subscription check failed for returning account:', error);
      }

      funnel.track(EVENTS.FUNNEL_EXISTING_ACCOUNT, { is_subscribed: isSubscribed });

      if (isSubscribed) {
        logger.log('👋 Existing subscriber — skipping funnel, redirecting home');
        funnel.trackImmediate(EVENTS.FUNNEL_COMPLETED, {
          outcome: 'existing_subscriber_home',
          is_existing_account: true,
          has_character: !!sessionStorage.getItem(PREVIEW_KEY),
        });
        clearIntroFunnelSession();
        setShowExistingAccount(true);
        redirectTimerRef.current = setTimeout(() => {
          window.location.replace('https://www.pixleyai.com');
        }, 3400);
        return;
      }

      logger.log('↩️ Existing account without an active subscription — continuing to checkout');
      setStep('finishing');
      await returnToFunnel({ isExisting: true });
      return;
    }

    setStep('finishing');

    // Best-effort: keep the character they just made (mirrors the iOS flow,
    // which uploads the held preview after sign-in). Never blocks the funnel.
    try {
      const previewDataUrl = sessionStorage.getItem(PREVIEW_KEY);
      const savedName = sessionStorage.getItem(NAME_KEY);
      if (previewDataUrl && savedName && user?.id) {
        await apiClient.saveFunnelCharacter({
          userId: user.id,
          name: savedName,
          animatedImage: dataURLtoBlob(previewDataUrl),
        });
        logger.log('✅ Intro funnel character saved to account');
      }
    } catch (error) {
      logger.warn('Intro funnel character save failed (non-blocking):', error);
    }

    // Best-effort: seed the account persona with everything the funnel learned
    // (child's name, screen-time struggle) in the same shape OnboardingContext
    // saves, so stories personalize from day one — and since a non-empty
    // persona marks onboarding as done, funnel users aren't re-onboarded
    // in-app. Existing personas are merged, not replaced.
    try {
      const personaName = sessionStorage.getItem(NAME_KEY);
      if (personaName && user?.id) {
        let existing = user.persona;
        if (typeof existing === 'string') {
          try {
            existing = JSON.parse(existing);
          } catch {
            existing = null;
          }
        }
        if (!existing || typeof existing !== 'object' || Array.isArray(existing)) existing = {};

        const persona = {
          favorite_shows: [],
          aggregated_topics: [],
          aggregated_interests: [],
          aggregated_preferences: [],
          profile_picture: null,
          discovery_source: [],
          ...existing,
          child_name: existing.child_name || personaName || null,
          // This funnel doesn't ask for an age either, but the key is still
          // written so an existing value survives the merge rather than being
          // dropped from the stored persona.
          child_age: existing.child_age || null,
          onboarding_source: existing.onboarding_source || 'web_funnel',
        };

        await apiClient.updateUserPersona(user.id, persona);
        logger.log('✅ Intro funnel persona saved to account');
      }
    } catch (error) {
      logger.warn('Intro funnel persona save failed (non-blocking):', error);
    }

    // Stamp the person so funnel sign-ups stay identifiable in cohorts long
    // after the funnel events themselves scroll out of the retention window.
    funnel.setPersonProperties({
      signup_source: 'web_funnel',
      funnel_name: 'web2app_intro',
    });

    await returnToFunnel({ isExisting: false });
  }, [apiClient, redirectUri, funnelState, user, setStep, funnel]);

  // Fires when the visitor lands back authenticated after the OAuth round trip
  // (step === 'signin'), or arrives already signed in before starting
  // (step === 'friend' untouched) — either way, finish and return to the funnel.
  useEffect(() => {
    if (loading || !isAuthenticated || !apiClient) return;
    if (hasCompletedRef.current) return;
    if (invalidLink) {
      logger.warn('Intro funnel auth: missing redirect_uri or state');
      return;
    }
    // Funnel data is waiting to be saved (character and/or persona) — hold
    // until the user object (and its id) has loaded so the saves aren't
    // silently skipped
    const hasFunnelData =
      sessionStorage.getItem(PREVIEW_KEY) ||
      sessionStorage.getItem(NAME_KEY);
    if (hasFunnelData && !user?.id) return;
    if (step === 'signin' || step === 'friend') {
      completeAndRedirect();
    }
  }, [isAuthenticated, loading, apiClient, invalidLink, step, user, completeAndRedirect]);

  // ── generation (same /previewCharacterImage the iOS onboarding calls) ────
  useEffect(() => {
    if (step !== 'generating') return undefined;
    // Ease toward 95%, slowing as it approaches — keeps the bar alive during an
    // open-ended wait without ever finishing early (NewOnboardingFlow)
    const id = setInterval(() => {
      setGenProgress((p) => (p >= 0.95 ? p : p + (0.95 - p) * 0.08));
    }, 500);
    return () => clearInterval(id);
  }, [step]);

  // Cycle the "Pixley is painting" status lines while generating
  useEffect(() => {
    if (step !== 'generating') return undefined;
    setGenMsgIndex(0);
    const id = setInterval(() => setGenMsgIndex((i) => (i + 1) % GEN_MESSAGES.length), 2600);
    return () => clearInterval(id);
  }, [step]);

  const startGeneration = async (image) => {
    setGenProgress(0);
    setStep('generating');

    const startedAt = Date.now();
    // Counts the /previewCharacterImage attempts this run actually spent, so a
    // slow-but-successful retry is distinguishable from a first-try success.
    let attempts = 0;
    funnel.track(EVENTS.FUNNEL_GENERATION_STARTED, {
      photo_size_kb: image?.size ? Math.round(image.size / 1024) : null,
    });

    try {
      let tokenResp;
      try {
        tokenResp = await apiClient.getFunnelPreviewToken(redirectUri, funnelState);
      } catch (error) {
        if (error.status === 429) {
          // Minting is IP rate-limited as a flood backstop; the generation
          // budget itself is charged later, on /previewCharacterImage.
          throw new Error("You've hit the character-preview limit for now. Please wait a bit and try again.");
        }
        throw error;
      }
      const previewToken = tokenResp.token || tokenResp.data?.token;
      if (!previewToken) throw new Error('Could not start character generation. Please try again.');

      // 2 auto-attempts max. Each one charges the per-IP budget
      // (WEB_PREVIEW_IP_MAX_PER_HOUR, 8/hour) as well as the per-session one,
      // so keep this small — it must leave room for manual "try a different
      // photo" do-overs before the visitor hits a 429.
      let lastError;
      for (let attempt = 0; attempt < 2; attempt++) {
        attempts += 1;
        try {
          const blob = await apiClient.previewCharacterImageWeb({
            name: childName,
            image,
            handdrawn: false,
            previewToken,
          });
          const dataUrl = await blobToDataURL(blob, 768);
          sessionStorage.setItem(PREVIEW_KEY, dataUrl);
          setPreviewImage(dataUrl);
          setGenProgress(1);
          setHeroRevealed(false);
          funnel.track(EVENTS.FUNNEL_GENERATION_COMPLETED, {
            duration_seconds: Math.round((Date.now() - startedAt) / 1000),
            attempts,
          });
          funnel.trackStepCompleted('create', { creation_method: 'photo' });
          setTimeout(() => setStep('meet'), 400);
          return;
        } catch (error) {
          lastError = error;
          // Only retry faults that might be transient. A 4xx is deterministic for
          // this input — safety block, blocked name, oversize photo, or a spent
          // preview budget (429) — so retrying returns the same answer AND spends
          // another slot from the per-IP budget. 5xx can be a blip, so retry those.
          if (error.status >= 400 && error.status < 500) break;
          if (/inappropriate|safety|copyright/i.test(error.message || '')) break;
          await new Promise((r) => setTimeout(r, 2000));
        }
      }
      throw lastError || new Error('Character generation failed');
    } catch (error) {
      logger.error('❌ Intro funnel preview generation failed:', error);
      funnel.track(EVENTS.FUNNEL_GENERATION_FAILED, {
        duration_seconds: Math.round((Date.now() - startedAt) / 1000),
        attempts,
        error_message: error.message || 'unknown',
        status_code: error.status || null,
        // Safety blocks and rate limits are the two failures worth splitting
        // out: one is content, the other is capacity.
        is_rate_limited: error.status === 429,
        is_safety_block: /inappropriate|safety|copyright/i.test(error.message || ''),
      });
      showToast(error.message || "We couldn't bring your character to life. Please try again.", 'error');
      setStep('create');
    }
  };

  const handleGenerate = () => {
    if (!photoFile) {
      showToast('Add a photo first!', 'warning');
      return;
    }
    startGeneration(photoFile);
  };

  // Picking a ready-made friend skips image generation entirely: no
  // /previewCharacterImage call, so it costs nothing, never charges the per-IP
  // preview budget, and can't fail on a safety block. The chosen art is written
  // into PREVIEW_KEY in exactly the shape a generated preview takes (768px JPEG
  // data URL), so the meet step, sign-in and saveFunnelCharacter downstream need
  // no special-casing — the character is still saved under the child's name, the
  // friend is just the artwork standing in for a photo.
  const choosePresetFriend = async (friend) => {
    if (loadingFriend) return;
    setLoadingFriend(friend.key);
    try {
      const response = await fetch(`/assets/funnel-friends/${friend.key}.webp`);
      if (!response.ok) throw new Error(`Could not load ${friend.name}`);
      const dataUrl = await blobToDataURL(await response.blob(), 768);
      sessionStorage.setItem(PREVIEW_KEY, dataUrl);
      setPreviewImage(dataUrl);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        friend_name: friend.name,
        success: true,
        // Whether the escape hatch is used instead of a photo, or after one
        // was already added, says different things about the photo ask.
        had_photo: !!photoFile,
      });
      funnel.trackStepCompleted('create', { creation_method: 'preset_friend' });
      // No progress bar to run down — straight to the reveal
      setHeroRevealed(false);
      setStep('meet');
    } catch (error) {
      logger.error('Preset friend selection failed:', error);
      funnel.track(EVENTS.FUNNEL_PRESET_FRIEND_SELECTED, {
        friend: friend.key,
        success: false,
        error_message: error.message || 'unknown',
      });
      showToast("We couldn't load that friend. Please try another.", 'error');
    } finally {
      setLoadingFriend(null);
    }
  };

  // `source` distinguishes the two hidden inputs ('upload' = gallery/file
  // picker, 'camera_native' = the mobile capture input) for analytics; the
  // desktop in-page camera reports itself from capturePhoto().
  const handlePhotoChange = (e, source = 'upload') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(URL.createObjectURL(file));
    funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
      method: source,
      // A replaced photo usually means the first attempt was rejected or
      // disliked — worth separating from a first add.
      is_replacement: !!photoFile,
      photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
      photo_type: file.type || null,
    });
  };

  // ── "Take photo" ──────────────────────────────────────────────────────────
  // The input's `capture` attribute only opens a camera on phones/tablets —
  // desktop browsers silently ignore it and show the file picker. On desktop
  // we open an in-page getUserMedia camera instead.
  const isTouchDevice =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.userAgent)); // iPadOS reports as Mac

  const openCamera = async () => {
    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
    } catch (error) {
      logger.warn('Camera unavailable, falling back to file picker:', error);
      showToast("We couldn't open your camera — choose a photo instead.", 'warning');
      fileInputRef.current?.click();
    }
  };

  const closeCamera = useCallback(() => {
    cameraStreamRef.current?.getTracks().forEach((t) => t.stop());
    cameraStreamRef.current = null;
    setCameraOpen(false);
  }, []);

  // Attach the stream once the modal's <video> is in the DOM
  useEffect(() => {
    if (cameraOpen && cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraOpen]);

  // Never leave the camera running on unmount
  useEffect(() => closeCamera, [closeCamera]);

  const capturePhoto = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setPhotoPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(file);
        });
        funnel.track(EVENTS.FUNNEL_PHOTO_ADDED, {
          method: 'camera_web',
          is_replacement: !!photoFile,
          photo_size_kb: file.size ? Math.round(file.size / 1024) : null,
          photo_type: file.type || null,
        });
        closeCamera();
      },
      'image/jpeg',
      0.92
    );
  };

  // Hero pop-in on the meet step (spring-ish scale, then confetti)
  useEffect(() => {
    if (step !== 'meet') return undefined;
    const id = setTimeout(() => setHeroRevealed(true), 120);
    return () => clearTimeout(id);
  }, [step]);

  const handleGoogleSignIn = async () => {
    try {
      logger.log('Starting Google OAuth (intro funnel)...');
      setIsSigningIn(true);
      // Last event this page load gets — signin_started vs. the post-OAuth
      // 'finishing' view is what measures Google round-trip abandonment.
      funnel.trackStepCompleted('signin', { method: 'google' }, { immediate: true });
      funnel.trackImmediate(EVENTS.FUNNEL_SIGNIN_STARTED, { method: 'google' });

      // redirect_uri/state ride back in the return URL below — no need to
      // stash them. Only the funnel's own answers stay in sessionStorage so
      // they can be saved to the account after the Google round trip.
      sessionStorage.setItem(STEP_KEY, 'signin');
      if (childName) sessionStorage.setItem(NAME_KEY, childName);

      // Land back in this funnel (not the site root) after Google, carrying the
      // funnel params in the URL so the flow resumes without depending on
      // storage. Prefixed names avoid colliding with the OAuth `state`/`code`
      // Supabase appends to this URL on return.
      //
      // The path is bare /funnel-auth, NOT this page's own URL: Supabase only
      // redirects to allowlisted URLs, and /funnel-auth is the one already
      // proven to be on that list — funnel one has been round-tripping through
      // it in production. `variant=intro` is what brings the visitor back to
      // this page rather than funnel one's (see FunnelAuthRoute in App.js).
      // Sending them to a new path would mean editing the Supabase allowlist,
      // and a URL missing from it fails silently, dropping the visitor on the
      // home page with their funnel state gone.
      const returnUrl = new URL('/funnel-auth', window.location.origin);
      returnUrl.searchParams.set('variant', 'intro');
      returnUrl.searchParams.set('funnel_redirect_uri', redirectUri);
      returnUrl.searchParams.set('funnel_state', funnelState);

      const timeoutId = setTimeout(() => {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, { method: 'google', reason: 'timeout' });
        showToast('Sign in is taking too long. Please try again.', 'error');
      }, 15000);

      const result = await signInWithGoogle(returnUrl.toString());
      clearTimeout(timeoutId);

      if (result && result.success) {
        logger.log('Google OAuth initiated successfully');
      } else {
        setIsSigningIn(false);
        funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
          method: 'google',
          reason: 'oauth_init_failed',
          error_message: result?.error || 'unknown',
        });
        showToast(result?.error || 'Sign in failed. Please try again.', 'error');
      }
    } catch (error) {
      logger.error('Google sign in error:', error);
      setIsSigningIn(false);
      funnel.track(EVENTS.FUNNEL_SIGNIN_FAILED, {
        method: 'google',
        reason: 'exception',
        error_message: error.message || 'unknown',
      });
      showToast(error.message || 'Failed to sign in with Google', 'error');
    }
  };

  const advance = (from, to) => {
    funnel.trackStepCompleted(from);
    setStep(to);
  };

  const canGenerate = !!photoFile;

  const stepIndex = STEP_ORDER.indexOf(
    step === 'generating' ? 'create' : step === 'finishing' ? 'signin' : step
  );
  const cardKey = invalidLink ? 'invalid' : step;

  // ── render ────────────────────────────────────────────────────────────────

  // Same welcome screen the other funnel opens on — a full-page takeover ahead
  // of the step shell, not a step of its own.
  if (showWelcome && !invalidLink) {
    return (
      <FunnelWelcomePage
        onContinue={() => {
          funnel.trackStepCompleted('welcome');
          setStep('friend');
          setShowWelcome(false);
        }}
      />
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <FunnelIntroStyles />
      <div className="fi-bg">
        <AmbientBackground />
        <div className="fi-shell">

          {/* Step header (segmented progress, like the iOS flow) */}
          {!invalidLink && step !== 'finishing' && (
            <motion.div
              className="fi-header"
              initial={{ opacity: 0, y: -14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="fi-back-slot">
                <AnimatePresence>
                  {BACK_TARGET[step] && (
                    <motion.button
                      className="fi-back"
                      onClick={() => {
                        funnel.track(EVENTS.FUNNEL_BACK_CLICKED, {
                          from_step: step,
                          to_step: BACK_TARGET[step],
                        });
                        setStep(BACK_TARGET[step]);
                      }}
                      aria-label="Back"
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      whileHover={{ scale: 1.08 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
              <div className="fi-segments">
                {STEP_ORDER.map((s, i) => (
                  <div key={s} className={`fi-segment ${i <= stepIndex ? 'filled' : ''}`} />
                ))}
              </div>
              <div className="fi-step-count">Step {Math.max(1, stepIndex + 1)} of {STEP_ORDER.length}</div>
            </motion.div>
          )}

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={cardKey}
              className={`fi-card fi-center ${cardKey === 'meet' ? 'fi-meet' : ''}`}
              custom={direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {invalidLink && (
                <>
                  <motion.div variants={itemVariants} className="fi-panda-wrap">
                    <img className="fi-panda fi-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">Hmm, this link isn't quite right</motion.h1>
                  <motion.p variants={itemVariants} className="fi-subtitle">
                    This page is part of the Pixley sign-up flow. Head to{' '}
                    <a href="https://www.pixleyai.com">pixleyai.com</a> to get started.
                  </motion.p>
                </>
              )}

              {/* SCREENS 1-3 — the explainer slides, straight off the landing page */}
              {!invalidLink && SLIDE_BY_KEY[step] && (
                <SlideScreen
                  slide={SLIDE_BY_KEY[step]}
                  clipIdx={clipIdx}
                  onContinue={() => advance(step, FLOW_ORDER[FLOW_ORDER.indexOf(step) + 1])}
                />
              )}


              {/* SCREEN 4 — name (NOChildNameStep) */}
              {!invalidLink && step === 'name' && (
                <>
                  <motion.div variants={itemVariants} className="fi-panda-wrap">
                    <img className="fi-panda fi-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.div variants={itemVariants} className="fi-eyebrow">Let's get started</motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">What's your child's name?</motion.h1>
                  <motion.p variants={itemVariants} className="fi-subtitle">
                    They're about to become the hero of their very own cartoons.
                  </motion.p>
                  <motion.div variants={itemVariants}>
                    <input
                      className="fi-input"
                      type="text"
                      value={childName}
                      maxLength={24}
                      placeholder="Their name"
                      onChange={(e) => {
                        setChildName(e.target.value);
                        sessionStorage.setItem(NAME_KEY, e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && childName.trim().length >= 2) submitName('enter_key');
                      }}
                    />
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <button
                      className="fi-primary-btn"
                      disabled={childName.trim().length < 2}
                      onClick={() => submitName('button')}
                    >
                      Continue <span className="fi-chevron">›</span>
                    </button>
                  </motion.div>
                </>
              )}

              {/* SCREEN 5 — add a photo (NOCreatePixleyStep) */}
              {!invalidLink && step === 'create' && (
                <>
                  <motion.div variants={itemVariants} className="fi-panda-wrap">
                    <img className="fi-panda fi-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                  </motion.div>
                  <motion.div variants={itemVariants} className="fi-eyebrow">Create their new friend</motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">Help us bring {childName.trim()} to life</motion.h1>
                  <motion.p variants={itemVariants} className="fi-subtitle">
                    Upload a photo and we'll turn {childName.trim()} into a cartoon hero.
                  </motion.p>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handlePhotoChange(e, 'camera_native')}
                    style={{ display: 'none' }}
                  />

                  {photoFile ? (
                    <motion.div variants={itemVariants}>
                      <button className="fi-photo-card" onClick={() => fileInputRef.current?.click()}>
                        <img src={photoPreviewUrl} alt="Selected" className="fi-photo-thumb" />
                        <span className="fi-photo-text">
                          <strong>Photo added</strong>
                          <small>Tap to change</small>
                        </span>
                        <span className="fi-photo-check"><Icon name="check" size={16} /></span>
                      </button>
                    </motion.div>
                  ) : (
                    <motion.div variants={itemVariants} className="fi-photo-options">
                      <button className="fi-photo-option" onClick={() => fileInputRef.current?.click()}>
                        <span className="fi-photo-option-icon"><Icon name="photo_library" size={30} /></span>
                        <strong>Upload photo</strong>
                        <small>Choose from gallery</small>
                      </button>
                      <button className="fi-photo-option" onClick={openCamera}>
                        <span className="fi-photo-option-icon"><Icon name="photo_camera" size={30} /></span>
                        <strong>Take photo</strong>
                        <small>Use your camera</small>
                      </button>
                    </motion.div>
                  )}

                  <motion.div variants={itemVariants}>
                    <button className="fi-primary-btn" disabled={!canGenerate} onClick={handleGenerate}>
                      Generate {childName.trim()} <span className="fi-chevron">›</span>
                    </button>
                  </motion.div>

                  {/* Escape hatch: no photo handy → start with a ready-made
                      friend. Deliberately below the primary CTA so the photo
                      path stays the obvious one. */}
                  <motion.div variants={itemVariants} className="fi-friends">
                    <div className="fi-friends-divider">
                      <span>or pick a ready-made friend</span>
                    </div>
                    <div className="fi-friends-row">
                      {PRESET_FRIENDS.map((friend) => (
                        <button
                          key={friend.key}
                          className={`fi-friend${loadingFriend === friend.key ? ' is-loading' : ''}`}
                          onClick={() => choosePresetFriend(friend)}
                          disabled={!!loadingFriend}
                          aria-label={`Use ${friend.name} the ${friend.kind}`}
                        >
                          <span className="fi-friend-art">
                            <img
                              src={`/assets/funnel-friends/${friend.key}.webp`}
                              alt=""
                              loading="lazy"
                              width="72"
                              height="72"
                              // Fall back to the full-size PNG the rest of the
                              // app uses if WebP 404s or fails to decode
                              onError={(e) => {
                                e.target.src = `/assets/${friend.key}.png`;
                              }}
                            />
                            {loadingFriend === friend.key && <span className="fi-friend-spinner" />}
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}

              {/* SCREEN 5b — generating (NOCharacterGenerationStep) */}
              {!invalidLink && step === 'generating' && (
                <>
                  <motion.div variants={itemVariants}>
                    <div className="fi-panda-glow">
                      <video className="fi-panda-video" autoPlay muted loop playsInline>
                        <source src="/assets/loading-panda.webm" type="video/webm" />
                        <source src="/assets/loading-panda.mp4" type="video/mp4" />
                      </video>
                    </div>
                  </motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">Bringing {childName.trim()} to life…</motion.h1>
                  <motion.div variants={itemVariants} className="fi-gen-msg">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={genMsgIndex}
                        className="fi-subtitle"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {GEN_MESSAGES[genMsgIndex]}
                      </motion.p>
                    </AnimatePresence>
                  </motion.div>
                  <motion.div variants={itemVariants} className="fi-progress-row">
                    <div className="fi-progress-track">
                      <div className="fi-progress-fill" style={{ width: `${Math.round(genProgress * 100)}%` }}>
                        <div className="fi-progress-shimmer" />
                      </div>
                    </div>
                    <span className="fi-progress-label">{Math.round(genProgress * 100)}%</span>
                  </motion.div>

                  {/* The one open-ended wait in the funnel — offer something to
                      watch rather than just the bar. Plays in an overlay, so the
                      generation request in flight here is never abandoned. */}
                  <FunnelWaitVideo
                    onPlay={(episode) =>
                      funnel.track(EVENTS.FUNNEL_WAIT_VIDEO_PLAYED, {
                        episode_id: episode?.id || null,
                        episode_name: episode?.name || null,
                        // Where in the wait they gave up on the bar
                        progress_percent: Math.round(genProgress * 100),
                      })
                    }
                  />
                </>
              )}

              {/* SCREEN 6 — meet the character (NOMeetPixleyStep) */}
              {!invalidLink && step === 'meet' && previewImage && (
                <>
                  {heroRevealed && <Confetti />}
                  <motion.div variants={itemVariants} className="fi-eyebrow">Meet your new friend</motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">This is {childName.trim()}!</motion.h1>
                  <motion.p variants={itemVariants} className="fi-subtitle">
                    The hero of every cartoon and bedtime story, made just for them.
                  </motion.p>
                  <div className={`fi-hero-frame ${heroRevealed ? 'revealed' : ''}`}>
                    <span className="fi-hero-halo" aria-hidden="true" />
                    <Sparkles />
                    <img src={previewImage} alt={childName.trim()} className="fi-hero-img" />
                  </div>
                  <motion.div variants={itemVariants}>
                    <button
                      className="fi-primary-btn"
                      onClick={() => {
                        funnel.trackStepCompleted('meet');
                        setStep('signin');
                      }}
                    >
                      Save {childName.trim()}'s character <span className="fi-chevron">›</span>
                    </button>
                  </motion.div>
                </>
              )}

              {/* SCREEN 7 — sign in */}
              {!invalidLink && step === 'signin' && (
                <>
                  <motion.div variants={itemVariants} className="fi-eyebrow">Last step</motion.div>
                  <motion.h1 variants={itemVariants} className="fi-title">
                    {childName.trim() ? `Save ${childName.trim()}'s character` : "Let's make their first story"}
                  </motion.h1>
                  <motion.p variants={itemVariants} className="fi-subtitle">
                    Create your account to keep this character and every story you make.
                  </motion.p>

                  {previewImage ? (
                    <motion.div variants={itemVariants} className="fi-signin-avatar-wrap">
                      <div className="fi-signin-avatar">
                        <img src={previewImage} alt={childName.trim()} />
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div variants={itemVariants} className="fi-panda-wrap">
                      <div className="fi-panda-halo">
                        <img className="fi-panda fi-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
                      </div>
                    </motion.div>
                  )}

                  {isSigningIn ? (
                    <div className="fi-signing-in">
                      <div className="fi-spinner" />
                      <span>Setting up your account…</span>
                    </div>
                  ) : (
                    <motion.div variants={itemVariants}>
                      <button className="fi-google-btn" onClick={handleGoogleSignIn}>
                        <svg className="fi-google-icon" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                      </button>
                      <p className="fi-legal">
                        By continuing you agree to our{' '}
                        <a href="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                      </p>
                    </motion.div>
                  )}
                </>
              )}

              {/* Finishing — returning to the funnel */}
              {!invalidLink && step === 'finishing' && (
                showSuccess ? (
                  <>
                    <div className="fi-success-check"><Icon name="check" size={42} /></div>
                    <h1 className="fi-title">You're all set!</h1>
                    <p className="fi-subtitle">Taking you back to finish signing up…</p>
                  </>
                ) : (
                  <>
                    <motion.div variants={itemVariants}>
                      <div className="fi-panda-glow">
                        <video className="fi-panda-video" autoPlay muted loop playsInline>
                          <source src="/assets/loading-panda.webm" type="video/webm" />
                          <source src="/assets/loading-panda.mp4" type="video/mp4" />
                        </video>
                      </div>
                    </motion.div>
                    <motion.h1 variants={itemVariants} className="fi-title">Setting up your account…</motion.h1>
                    <motion.p variants={itemVariants} className="fi-subtitle">
                      {childName.trim() ? `Saving ${childName.trim()}'s character to your account.` : 'Just a moment.'}
                    </motion.p>
                  </>
                )
              )}
            </motion.div>
          </AnimatePresence>

          <motion.p
            className="fi-footer-note"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            PIXLEY
          </motion.p>

        </div>

        {/* In-page camera (desktop, where the capture attribute is ignored) */}
        <AnimatePresence>
          {cameraOpen && (
            <motion.div
              className="fi-camera-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div
                className="fi-camera-modal"
                initial={{ opacity: 0, scale: 0.92, y: 18 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 8 }}
                transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              >
                <video ref={cameraVideoRef} className="fi-camera-video" autoPlay playsInline muted />
                <div className="fi-camera-actions">
                  <button className="fi-camera-cancel" onClick={closeCamera}>Cancel</button>
                  <button className="fi-camera-shutter" onClick={capturePhoto} aria-label="Take photo" />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Existing account — welcome back, then off to the main site */}
        <AnimatePresence>
          {showExistingAccount && (
            <motion.div
              className="fi-exist-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                className="fi-exist-modal"
                role="dialog"
                aria-modal="true"
                aria-label="You already have an account"
                initial={{ opacity: 0, scale: 0.82, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              >
                <span className="fi-exist-glow" aria-hidden="true" />
                <motion.img
                  className="fi-panda"
                  src="/assets/pixley-panda-nobg.png"
                  alt="Pixley Panda"
                  initial={{ scale: 0, rotate: -14 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 15, delay: 0.15 }}
                />
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="fi-eyebrow">Welcome back</div>
                  <h1 className="fi-title">You already have an account!</h1>
                  <p className="fi-subtitle">No need to sign up again — we're taking you back to Pixley.</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  <div className="fi-exist-progress" aria-hidden="true"><div /></div>
                  <button
                    className="fi-primary-btn"
                    onClick={() => window.location.replace('https://www.pixleyai.com')}
                  >
                    Take me there now <span className="fi-chevron">›</span>
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </MotionConfig>
  );
};

// Same iOS onboarding theme as FunnelAuthPage (NewOnboardingKit.swift):
// bg #F5FAFF→#E8F1FF, ink #0B1A38, subtitle #6C7A8F, accent #0A6CFF,
// card stroke #E8EDF5, track #DDE6F2, rounded Fredoka titles.
const FunnelIntroStyles = () => (
  <style>{`
    .fi-bg {
      position: relative;
      min-height: 100vh;
      min-height: 100dvh;
      background:
        radial-gradient(1100px 750px at 85% -10%, rgba(124,92,255,0.10), transparent 60%),
        radial-gradient(950px 700px at -10% 110%, rgba(10,108,255,0.10), transparent 60%),
        linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(32px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(32px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }

    /* Touch polish — no gray tap flash, no double-tap zoom delay */
    .fi-primary-btn, .fi-google-btn, .fi-back, .fi-photo-option, .fi-photo-card,
    .fi-camera-shutter, .fi-camera-cancel, .fi-input {
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
    }

    /* Ambient animated background (blobs drift, sparkle dots rise) */
    .fi-ambient {
      position: fixed; inset: 0;
      overflow: hidden;
      pointer-events: none;
      z-index: 0;
    }
    .fi-blob { position: absolute; border-radius: 50%; filter: blur(70px); }
    .fi-blob-1 {
      width: 500px; height: 500px; top: -180px; left: -140px;
      background: rgba(10,108,255,0.14);
      animation: fiDrift1 24s ease-in-out infinite;
    }
    .fi-blob-2 {
      width: 440px; height: 440px; bottom: -160px; right: -120px;
      background: rgba(124,92,255,0.12);
      animation: fiDrift2 28s ease-in-out infinite;
    }
    .fi-blob-3 {
      width: 380px; height: 380px; top: 30%; right: -100px;
      background: rgba(255,200,61,0.12);
      animation: fiDrift3 32s ease-in-out infinite;
    }
    @keyframes fiDrift1 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(70px, 50px) scale(1.12); }
    }
    @keyframes fiDrift2 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-60px, -70px) scale(1.08); }
    }
    @keyframes fiDrift3 {
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(-80px, 40px) scale(1.15); }
    }
    .fi-dot {
      position: absolute; bottom: -12px;
      border-radius: 50%;
      opacity: 0;
      animation: fiDotRise linear infinite;
    }
    @keyframes fiDotRise {
      0% { transform: translateY(0) scale(1); opacity: 0; }
      8% { opacity: 0.7; }
      85% { opacity: 0.5; }
      100% { transform: translateY(-105vh) scale(0.6); opacity: 0; }
    }

    /* Shell centers itself; margin:auto degrades to scroll when the content is
       taller than the screen */
    .fi-shell {
      width: 100%; max-width: 680px;
      margin: auto;
      position: relative; z-index: 1;
    }

    /* Step header */
    .fi-header {
      display: flex;
      align-items: center;
      gap: 14px;
      margin: 0 4px 22px;
    }
    .fi-back-slot {
      width: 40px; height: 40px; flex-shrink: 0;
      transition: width 0.35s cubic-bezier(.22,1,.36,1);
    }
    .fi-back {
      width: 40px; height: 40px;
      display: flex; align-items: center; justify-content: center;
      background: #FFF;
      border: 1px solid #E8EDF5;
      border-radius: 14px;
      color: #0B1A38;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(11,26,51,0.06);
      transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
    }
    .fi-back:hover {
      border-color: rgba(10,108,255,0.35);
      box-shadow: 0 4px 14px rgba(10,108,255,0.15);
      color: #0A6CFF;
    }
    .fi-segments { display: flex; gap: 6px; flex: 1; }
    .fi-segment {
      height: 6px; flex: 1; border-radius: 3px;
      background: #DDE6F2;
      overflow: hidden;
    }
    .fi-segment::after {
      content: '';
      display: block;
      height: 100%; width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #4D94FF);
      transition: width 0.6s cubic-bezier(.22,1,.36,1);
    }
    .fi-segment.filled::after { width: 100%; }
    .fi-step-count {
      font-size: 13px; font-weight: 600; color: #6C7A8F; white-space: nowrap;
    }

    /* Card */
    .fi-card {
      position: relative;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(232,237,245,0.9);
      border-radius: 28px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.10), 0 4px 14px rgba(11,26,51,0.05);
      padding: 40px 32px 36px;
    }
    .fi-center { text-align: center; }

    .fi-footer-note {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 700;
      color: #8A97AB;
      letter-spacing: 3px;
      margin: 18px 0 0;
    }

    /* Type */
    .fi-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 2px; text-transform: uppercase;
      color: #0A6CFF; margin-bottom: 10px;
    }
    .fi-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 31px; font-weight: 700;
      color: #0B1A38; line-height: 1.15;
      letter-spacing: -0.3px;
      margin: 0 0 10px;
    }
    .fi-subtitle {
      font-size: 16px; color: #6C7A8F; line-height: 1.55;
      margin: 0 0 24px;
    }
    .fi-subtitle a { color: #0A6CFF; font-weight: 600; }

    /* Panda mascot */
    .fi-panda-wrap { display: flex; justify-content: center; }
    .fi-panda {
      width: 118px; height: 118px;
      object-fit: contain;
      margin-bottom: 10px;
      filter: drop-shadow(0 10px 18px rgba(11,26,51,0.14));
    }
    .fi-float { animation: fiFloat 2.6s ease-in-out infinite; }
    @keyframes fiFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-7px); }
    }
    /* Sign-in mascot sits on a soft radial glow so the screen with no artwork
       of its own still has a focal point */
    .fi-panda-halo {
      display: grid; place-items: center;
      margin: 0 auto 14px;
      width: 178px; height: 158px;
      background: radial-gradient(circle at 50% 55%, rgba(10,108,255,0.20) 0%, rgba(10,108,255,0) 70%);
    }
    .fi-panda-halo .fi-panda { margin-bottom: 0; }
    .fi-panda-glow {
      width: 150px; height: 150px; margin: 0 auto 12px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.16) 0%, rgba(10,108,255,0) 70%);
      display: flex; align-items: center; justify-content: center;
      animation: fiPulse 1.9s ease-in-out infinite;
    }
    @keyframes fiPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .fi-panda-video { width: 140px; height: 140px; object-fit: contain; }

    /* ── Explainer slides (screens 1-3) ────────────────────────────────────
       The landing page runs these as two-column sections; here they stack, and
       with no body copy left the clip is the screen. Every slide's media
       therefore breaks out past the card's side padding to run nearly
       edge-to-edge — the card is only there to hold the headline and button. */
    .fi-slide-stage {
      margin-bottom: 26px;
      width: calc(100% + 52px);
      margin-left: -26px;
    }

    /* Opaque clips (slides one and two) get the landing page's card framing:
       rounded, hairline border, soft drop shadow, and a slight resting tilt. */
    .fi-slide-frame {
      border-radius: 24px;
      /* drop-shadow rather than box-shadow: this element carries the resting
         tilt, and a drop-shadow follows the rotated shape instead of sitting
         square behind it. */
      filter: drop-shadow(0px 14px 30px rgba(11,26,51,0.16));
    }
    .fi-slide-video {
      display: block;
      width: 100%;
      height: auto;
      border-radius: 24px;
      border: 2px solid rgba(11,26,51,0.08);
      background: #EAF3FF;
    }

    /* Alpha-channel clips (slide three) have no frame — they float on the card
       over a soft stage glow and a ground shadow, as on the landing page. They
       have transparent margins baked into the source, so running them right to
       the card edge reads as the right size rather than as overflow. */
    .fi-clipstage {
      position: relative;
      /* Taller than the framed clips on slides one and two: this one is a
         portrait tablet standing upright, so height is what makes it read as
         big, where the others gain from width.

         Capped against the viewport as well as in pixels: at a flat 560px the
         card outgrew a 900px-tall desktop window and pushed the CTA below the
         fold. The vh term only bites on short windows — from ~1120px tall the
         pixel cap wins. */
      height: min(560px, 50vh);
      display: flex; align-items: center; justify-content: center;
    }
    .fi-clipstage-glow {
      position: absolute; inset: 6% 14%;
      background: radial-gradient(closest-side, rgba(10,108,255,0.16), transparent 72%);
      filter: blur(28px);
      pointer-events: none;
    }
    .fi-clipstage-ground {
      position: absolute; bottom: 2%; left: 50%;
      transform: translateX(-50%);
      width: 52%; height: 22px;
      background: radial-gradient(closest-side, rgba(11,26,51,0.18), transparent);
      filter: blur(9px);
      pointer-events: none;
    }
    .fi-clipstage-clip {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      pointer-events: none;
      filter: drop-shadow(0px 18px 28px rgba(11,26,51,0.18));
    }
    .fi-clipstage-clip video {
      max-height: 100%; max-width: 100%;
      /* the source clips have square corners baked in — round them off so only
         the tablet shell shows */
      border-radius: 7.5% / 5.7%;
    }

    /* The gradient word the landing page paints in each headline */
    .fi-accent {
      font-weight: 700;
      background: linear-gradient(92deg, #0A6CFF 0%, #00CAFF 110%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .fi-slide-title {
      letter-spacing: -0.02em;
      font-size: 34px;
      margin-bottom: 26px;
    }


    /* Buttons */
    .fi-primary-btn {
      position: relative;
      width: 100%;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      padding: 17px 22px;
      background: linear-gradient(135deg, #0A6CFF 0%, #3D8BFF 60%, #5EA1FF 100%);
      color: #FFF;
      font-size: 18px; font-weight: 700;
      font-family: 'Fredoka', 'Inter', sans-serif;
      border: none; border-radius: 18px;
      cursor: pointer;
      overflow: hidden;
      box-shadow: 0 8px 20px rgba(10,108,255,0.30), inset 0 1px 0 rgba(255,255,255,0.25);
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, opacity 0.2s;
    }
    .fi-primary-btn::before {
      content: '';
      position: absolute; top: 0; left: -80%;
      width: 60%; height: 100%;
      background: linear-gradient(105deg, transparent, rgba(255,255,255,0.35), transparent);
      transform: skewX(-20deg);
      transition: left 0.6s ease;
    }
    .fi-primary-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px rgba(10,108,255,0.40), inset 0 1px 0 rgba(255,255,255,0.25);
    }
    .fi-primary-btn:hover::before { left: 130%; }
    .fi-primary-btn:active { transform: scale(0.97); }
    .fi-chevron { font-size: 20px; line-height: 1; transition: transform 0.2s ease; }
    .fi-primary-btn:hover .fi-chevron { transform: translateX(3px); }

    .fi-google-btn {
      width: 100%;
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 15px 24px;
      background: #FFF;
      border: 1px solid #E8EDF5;
      border-radius: 18px;
      font-size: 16px; font-weight: 600;
      font-family: 'Inter', sans-serif;
      color: #0B1A38;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(11,26,51,0.06);
      transition: transform 150ms cubic-bezier(.2,.8,.2,1), border-color 0.2s, box-shadow 0.2s;
    }
    .fi-google-btn:hover {
      transform: translateY(-2px);
      border-color: rgba(10,108,255,0.30);
      box-shadow: 0 8px 20px rgba(10,108,255,0.14);
    }
    .fi-google-btn:active { transform: scale(0.98); }
    .fi-google-icon { width: 22px; height: 22px; }

    .fi-primary-btn:focus-visible, .fi-google-btn:focus-visible,
    .fi-back:focus-visible, .fi-photo-option:focus-visible,
    .fi-photo-card:focus-visible {
      outline: 3px solid rgba(10,108,255,0.4);
      outline-offset: 2px;
    }

    .fi-legal {
      margin: 14px 0 0;
      font-size: 12px; line-height: 1.5;
      color: #8A97AB;
    }
    .fi-legal a { color: #6C7A8F; text-decoration: underline; }
    .fi-legal a:hover { color: #0A6CFF; }

    .fi-signing-in {
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 15px;
      font-size: 16px; font-weight: 600; color: #0B1A38;
    }
    .fi-spinner {
      width: 22px; height: 22px;
      border: 3px solid #DDE6F2;
      border-top-color: #0A6CFF;
      border-radius: 50%;
      animation: fiSpin 0.8s linear infinite;
    }
    @keyframes fiSpin { to { transform: rotate(360deg); } }

    /* Success */
    .fi-success-check {
      position: relative;
      width: 80px; height: 80px;
      margin: 0 auto 18px;
      border-radius: 50%;
      background: linear-gradient(135deg, #3EDC8F, #20A96A);
      color: #FFF;
      font-size: 34px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 10px 26px rgba(32,169,106,0.35);
      animation: fiSuccessPop 0.6s ease-out;
    }
    .fi-success-check::after {
      content: '';
      position: absolute; inset: 0;
      border-radius: 50%;
      border: 3px solid rgba(62,220,143,0.5);
      animation: fiRipple 1.4s ease-out infinite;
    }
    @keyframes fiSuccessPop {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes fiRipple {
      from { transform: scale(1); opacity: 1; }
      to { transform: scale(1.7); opacity: 0; }
    }

    /* Existing-account modal */
    .fi-exist-overlay {
      position: fixed; inset: 0; z-index: 70;
      background: rgba(11,26,51,0.55);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fi-exist-modal {
      position: relative;
      width: min(430px, 100%);
      background: #FFF;
      border-radius: 28px;
      padding: 36px 28px 28px;
      text-align: center;
      overflow: hidden;
      box-shadow: 0 30px 80px rgba(11,26,51,0.35);
    }
    .fi-exist-glow {
      position: absolute; top: -70px; left: 50%;
      width: 260px; height: 260px;
      transform: translateX(-50%);
      border-radius: 50%;
      background: radial-gradient(circle, rgba(10,108,255,0.14) 0%, rgba(10,108,255,0) 70%);
      pointer-events: none;
    }
    .fi-exist-modal .fi-panda { display: block; margin: 0 auto 10px; }
    .fi-exist-modal .fi-title { font-size: 26px; }
    .fi-exist-modal .fi-subtitle { margin-bottom: 20px; }
    .fi-exist-progress {
      height: 6px;
      background: #E8EDF5;
      border-radius: 3px;
      overflow: hidden;
      margin: 0 0 18px;
    }
    .fi-exist-progress div {
      height: 100%;
      width: 0;
      border-radius: 3px;
      background: linear-gradient(90deg, #0A6CFF, #7C5CFF);
      animation: fiExistCountdown 3.4s linear forwards;
    }
    @keyframes fiExistCountdown { to { width: 100%; } }

    /* ── Steps copied from /funnel-auth (name → create → meet) ───────────
       Same rules as the .fa-* originals, renamed into this page's namespace so
       the two funnels can be restyled independently. */
    /* Inputs */
    .fi-input {
      width: 100%;
      padding: 16px 18px;
      font-size: 18px; font-weight: 600;
      font-family: 'Fredoka', 'Inter', sans-serif;
      color: #0B1A38;
      text-align: center;
      background: #F7FAFF;
      border: 2px solid #E8EDF5;
      border-radius: 16px;
      outline: none;
      margin-bottom: 20px;
      box-sizing: border-box;
      transition: border-color 0.2s, box-shadow 0.25s, background 0.2s;
    }
    .fi-input:focus {
      border-color: #0A6CFF;
      background: #FFF;
      box-shadow: 0 0 0 4px rgba(10,108,255,0.13);
    }
    .fi-input::placeholder { color: #A9B6C9; font-weight: 500; }

    /* Photo options (Upload / Take photo, like NOCreatePixleyStep) */
    .fi-photo-options {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-bottom: 20px;
    }
    .fi-photo-option {
      display: flex; flex-direction: column; align-items: center; gap: 4px;
      padding: 24px 12px;
      background: #F7FAFF;
      border: 2px dashed #C9D8EE;
      border-radius: 18px;
      cursor: pointer;
      color: #0B1A38;
      font-family: 'Inter', sans-serif;
      transition: border-color 0.2s, transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s, box-shadow 0.2s;
    }
    .fi-photo-option:hover {
      border-color: #0A6CFF;
      background: #FFF;
      transform: translateY(-3px);
      box-shadow: 0 10px 22px rgba(10,108,255,0.12);
    }
    .fi-photo-option:active { transform: translateY(-1px) scale(0.98); }
    .fi-photo-option-icon {
      margin-bottom: 4px;
      color: #0A6CFF;
      display: flex; align-items: center; justify-content: center;
      transition: transform 0.25s cubic-bezier(.34,1.5,.5,1);
    }
    .fi-photo-option:hover .fi-photo-option-icon { transform: scale(1.18) rotate(-5deg); }
    .fi-photo-option strong { font-size: 15px; }
    .fi-photo-option small { font-size: 12px; color: #6C7A8F; }

    /* Ready-made friends (secondary path when there's no photo to hand) */
    .fi-friends { margin-top: 22px; }
    .fi-friends-divider {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 14px;
      font-family: 'Inter', sans-serif;
      font-size: 12.5px; font-weight: 600;
      color: #8494AB;
      letter-spacing: 0.01em;
    }
    .fi-friends-divider::before, .fi-friends-divider::after {
      content: ''; flex: 1; height: 1px;
      background: linear-gradient(90deg, transparent, #D9E4F3, transparent);
    }
    /* Art only, no captions — centered cluster rather than a stretched grid,
       which left three lone circles marooned across the full card width */
    .fi-friends-row {
      display: flex;
      justify-content: center;
      gap: 14px;
    }
    .fi-friend {
      display: flex; align-items: center; justify-content: center;
      padding: 5px;
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: #0B1A38;
      font-family: 'Inter', sans-serif;
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), background 0.2s;
    }
    .fi-friend:hover:not(:disabled) { background: #F7FAFF; transform: translateY(-3px); }
    .fi-friend:active:not(:disabled) { transform: translateY(-1px) scale(0.97); }
    /* While one friend loads, only the others dim */
    .fi-friend:disabled { cursor: default; opacity: 0.5; }
    .fi-friend.is-loading { opacity: 1; }
    .fi-friend-art {
      position: relative;
      width: 92px; height: 92px;
      border-radius: 50%;
      overflow: hidden;
      background: #F1F6FF;
      box-shadow: 0 0 0 2px #E3ECF9, 0 4px 10px rgba(11,26,51,0.08);
      transition: box-shadow 0.2s;
    }
    .fi-friend:hover:not(:disabled) .fi-friend-art {
      box-shadow: 0 0 0 2px #0A6CFF, 0 8px 18px rgba(10,108,255,0.18);
    }
    .fi-friend-art img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fi-friend-spinner {
      position: absolute; inset: 0;
      background: rgba(255,255,255,0.72);
      display: block;
    }
    .fi-friend-spinner::after {
      content: '';
      position: absolute; top: 50%; left: 50%;
      width: 20px; height: 20px; margin: -10px 0 0 -10px;
      border: 2.5px solid rgba(10,108,255,0.25);
      border-top-color: #0A6CFF;
      border-radius: 50%;
      animation: fi-friend-spin 0.7s linear infinite;
    }
    @keyframes fi-friend-spin { to { transform: rotate(360deg); } }

    .fi-photo-card {
      width: 100%;
      display: flex; align-items: center; gap: 14px;
      padding: 12px 16px;
      margin-bottom: 20px;
      background: #FFF;
      border: 2px solid #0A6CFF;
      border-radius: 16px;
      cursor: pointer;
      text-align: left;
      box-shadow: 0 4px 14px rgba(10,108,255,0.10);
      transition: transform 0.18s cubic-bezier(.2,.8,.2,1), box-shadow 0.2s;
    }
    .fi-photo-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(10,108,255,0.16);
    }
    .fi-photo-thumb {
      width: 52px; height: 52px;
      border-radius: 12px;
      object-fit: cover;
    }
    .fi-photo-text { flex: 1; display: flex; flex-direction: column; }
    .fi-photo-text strong { font-size: 16px; color: #0B1A38; }
    .fi-photo-text small { font-size: 13px; color: #6C7A8F; }
    .fi-photo-check {
      width: 24px; height: 24px;
      background: #34C759;
      color: #FFF;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700;
      animation: fiCheckPop 0.45s cubic-bezier(.34,1.5,.5,1);
    }
    @keyframes fiCheckPop {
      from { transform: scale(0); }
      to { transform: scale(1); }
    }

    /* In-page camera modal (desktop fallback for the capture attribute) */
    .fi-camera-overlay {
      position: fixed; inset: 0; z-index: 60;
      background: rgba(11,26,51,0.72);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }
    .fi-camera-modal {
      width: min(560px, 100%);
      background: #FFF;
      border-radius: 24px;
      padding: 16px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.35);
    }
    .fi-camera-video {
      width: 100%;
      border-radius: 16px;
      transform: scaleX(-1); /* mirror the live preview like a selfie cam */
      background: #0B1A38;
    }
    .fi-camera-actions {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      padding-top: 14px;
    }
    .fi-camera-cancel {
      position: absolute; left: 8px;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 15px; font-weight: 600; color: #6C7A8F;
      cursor: pointer;
      transition: color 0.2s;
    }
    .fi-camera-cancel:hover { color: #0B1A38; }
    .fi-camera-shutter {
      width: 58px; height: 58px;
      border-radius: 50%;
      background: #0A6CFF;
      border: 4px solid #FFF;
      box-shadow: 0 0 0 3px #0A6CFF;
      cursor: pointer;
      transition: transform 0.15s cubic-bezier(.2,.8,.2,1);
    }
    .fi-camera-shutter:hover { transform: scale(1.08); }
    .fi-camera-shutter:active { transform: scale(0.92); }

    /* Progress bar (NOProgressBar with shimmer) */
    .fi-gen-msg {
      min-height: 50px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 18px;
    }
    .fi-gen-msg .fi-subtitle { margin: 0; }
    .fi-progress-row { display: flex; align-items: center; gap: 12px; }
    .fi-progress-track {
      flex: 1; height: 12px;
      background: #DDE6F2;
      border-radius: 6px;
      overflow: hidden;
    }
    .fi-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #0A6CFF, #4D94FF);
      border-radius: 6px;
      position: relative;
      overflow: hidden;
      transition: width 0.5s ease-in-out;
    }
    .fi-progress-shimmer {
      position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%);
      animation: fiShimmer 1.4s linear infinite;
    }
    @keyframes fiShimmer {
      from { transform: translateX(-100%); }
      to { transform: translateX(100%); }
    }
    .fi-progress-label {
      font-size: 15px; font-weight: 700; color: #0B1A38;
      font-variant-numeric: tabular-nums;
      min-width: 44px; text-align: right;
    }

    /* Meet step — hero reveal */
    .fi-meet { overflow: visible; }
    .fi-hero-frame {
      position: relative;
      width: 340px; height: 340px;
      margin: 8px auto 30px;
      border-radius: 40px;
      border: 5px solid #FFF;
      box-shadow: 0 16px 44px rgba(11,26,51,0.18);
      overflow: visible;
      transform: scale(0);
      opacity: 0;
    }
    .fi-hero-frame.revealed { animation: fiHeroPop 0.7s cubic-bezier(0.34, 1.45, 0.5, 1) forwards; }
    @keyframes fiHeroPop {
      0% { transform: scale(0); opacity: 0; }
      70% { transform: scale(1.06); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    .fi-hero-halo {
      position: absolute; inset: -24px;
      border-radius: 50%;
      background: conic-gradient(from 0deg,
        rgba(10,108,255,0.30), rgba(124,92,255,0.22), rgba(255,200,61,0.26),
        rgba(255,111,165,0.22), rgba(10,108,255,0.30));
      filter: blur(24px);
      animation: fiHaloSpin 9s linear infinite;
      z-index: 0;
    }
    @keyframes fiHaloSpin { to { transform: rotate(360deg); } }
    .fi-hero-img {
      position: relative; z-index: 1;
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 35px;
      display: block;
    }
    .fi-sparkle {
      position: absolute;
      animation: fiTwinkle 1.1s ease-in-out infinite alternate;
      pointer-events: none;
      z-index: 2;
    }
    @keyframes fiTwinkle {
      from { transform: scale(0.7); opacity: 0.4; }
      to { transform: scale(1.2); opacity: 1; }
    }

    /* Confetti */
    .fi-confetti {
      position: absolute; inset: 0;
      pointer-events: none;
      overflow: hidden;
      border-radius: 28px;
      z-index: 1;
    }
    .fi-confetti span {
      position: absolute;
      top: -20px;
      width: 11px; height: 11px;
      border-radius: 2px;
      animation-name: fiConfettiFall;
      animation-timing-function: ease-in;
      animation-fill-mode: forwards;
    }
    @keyframes fiConfettiFall {
      to {
        transform: translate(var(--drift), 640px) rotate(var(--spin));
        opacity: 0;
      }
    }

    /* Sign-in step avatar */
    .fi-signin-avatar-wrap { display: flex; justify-content: center; }
    .fi-signin-avatar {
      width: 230px; height: 230px;
      margin: 4px auto 26px;
      border-radius: 50%;
      padding: 5px;
      background: linear-gradient(135deg, #0A6CFF, #7C5CFF, #FFC83D);
      box-sizing: border-box;
      animation: fiAvatarGlow 3s ease-in-out infinite;
    }
    .fi-signin-avatar img {
      width: 100%; height: 100%;
      object-fit: cover;
      border-radius: 50%;
      border: 4px solid #FFF;
      box-sizing: border-box;
      display: block;
    }
    @keyframes fiAvatarGlow {
      0%, 100% { box-shadow: 0 12px 32px rgba(10,108,255,0.28); }
      50% { box-shadow: 0 14px 42px rgba(124,92,255,0.38); }
    }

    /* Mobile */
    @media (max-width: 480px) {
      .fi-bg {
        padding: calc(20px + env(safe-area-inset-top, 0px))
                 calc(12px + env(safe-area-inset-right, 0px))
                 calc(20px + env(safe-area-inset-bottom, 0px))
                 calc(12px + env(safe-area-inset-left, 0px));
      }
      .fi-card { padding: 30px 20px 28px; border-radius: 24px; }
      .fi-confetti { border-radius: 24px; }
      .fi-title { font-size: 26px; }
      .fi-subtitle { font-size: 15px; }
      .fi-hero-frame { width: 290px; height: 290px; }
      .fi-signin-avatar { width: 200px; height: 200px; }
      .fi-step-count { display: none; }
      .fi-panda { width: 100px; height: 100px; }
      .fi-panda-halo { width: 152px; height: 136px; }

      /* Comfortable 44px thumb target; the empty slot collapses so the
         progress bar is centered on steps without a back button */
      .fi-header { gap: 12px; margin-bottom: 18px; }
      .fi-back-slot { width: 44px; height: 44px; }
      .fi-back { width: 44px; height: 44px; }
      .fi-back-slot:empty { width: 0; }

      /* Cheaper compositing on phone GPUs */
      .fi-blob { filter: blur(50px); }
      .fi-blob-3 { display: none; }

      .fi-clipstage { height: 440px; }
      .fi-slide-stage { margin-bottom: 20px; width: calc(100% + 40px); margin-left: -20px; }
      .fi-slide-title { font-size: 27px; margin-bottom: 20px; }
      .fi-photo-option { padding: 20px 10px; }
      .fi-friends { margin-top: 18px; }
      .fi-friend-art { width: 84px; height: 84px; }
      .fi-primary-btn { padding: 16px 20px; font-size: 17px; }
      .fi-google-btn { padding: 14px 20px; font-size: 15px; }
      .fi-footer-note { font-size: 12px; margin-top: 14px; }
    }

    /* Small phones (iPhone SE, compact Androids) */
    @media (max-width: 374px) {
      .fi-card { padding: 26px 16px 24px; }
      .fi-title { font-size: 23px; }
      .fi-panda { width: 88px; height: 88px; }
      /* Three chips no longer fit on one line — let the row wrap tighter
         rather than shrink the text below readable size */
      .fi-clipstage { height: 380px; }
      .fi-slide-stage { width: calc(100% + 32px); margin-left: -16px; }
      .fi-slide-title { font-size: 24px; margin-bottom: 18px; }
      .fi-hero-frame { width: 250px; height: 250px; }
      .fi-signin-avatar { width: 175px; height: 175px; }
      .fi-photo-options { gap: 10px; }
      .fi-photo-option-icon { font-size: 26px; }
      .fi-photo-option strong { font-size: 14px; }
      .fi-friends-row { gap: 10px; }
      .fi-friend-art { width: 74px; height: 74px; }
      .fi-friends-divider { font-size: 11.5px; }
    }

    /* Short viewports (landscape phones, small screens): compress the tall
       decorative pieces so the card fits without scroll */
    @media (max-height: 700px) {
      .fi-bg { padding-top: 14px; padding-bottom: 14px; }
      .fi-header { margin-bottom: 12px; }
      .fi-card { padding: 24px 22px 22px; }
      .fi-title { font-size: 24px; }
      .fi-subtitle { margin-bottom: 16px; }
      .fi-panda { width: 78px; height: 78px; margin-bottom: 6px; }
      .fi-panda-halo { width: 122px; height: 108px; margin-bottom: 10px; }
      .fi-panda-glow { width: 112px; height: 112px; margin-bottom: 8px; }
      .fi-panda-video { width: 104px; height: 104px; }
      .fi-clipstage { height: 290px; }
      .fi-slide-stage { margin-bottom: 14px; width: calc(100% + 44px); margin-left: -22px; }
      .fi-slide-title { font-size: 25px; margin-bottom: 14px; }
      .fi-hero-frame { width: 210px; height: 210px; margin-bottom: 18px; }
      .fi-signin-avatar { width: 150px; height: 150px; margin-bottom: 18px; }
      .fi-gen-msg { min-height: 42px; margin-bottom: 14px; }
      .fi-footer-note { display: none; }
    }

    /* Touch devices — bigger camera shutter for thumbs */
    @media (pointer: coarse) {
      .fi-camera-shutter { width: 66px; height: 66px; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fi-float, .fi-panda-glow, .fi-blob, .fi-dot,
      .fi-progress-shimmer, .fi-sparkle, .fi-hero-halo, .fi-signin-avatar,
      .fi-photo-check, .fi-success-check, .fi-success-check::after { animation: none; }
      /* Keep the loading spinner — it's feedback, not decoration — but drop the
         hover lifts that reduced-motion users don't want */
      .fi-friend:hover:not(:disabled) { transform: none; }
      .fi-hero-frame.revealed { animation: none; transform: scale(1); opacity: 1; }
      .fi-confetti { display: none; }
      .fi-primary-btn:hover, .fi-google-btn:hover { transform: none; }
      .fi-primary-btn::before { display: none; }
    }
  `}</style>
);

export default FunnelIntroAuthPage;
